/**
 * 集团空调运行数据口径（本地复制，不引用空调设备管理页面）。
 *
 * 只读来源：
 * - /api/ebc-content/airInfo/pageAir          设备状态快照（含 state / powerStatus / roomTemperature）
 * 严禁在本文件或调用方引入 register/control/lock/scheduledTask/airGateway 等写接口。
 *
 * 口径：
 * 1. 总量、离线数优先取设备统计（与顶部“设备在线率”同一口径），运行/待机取空调设备列表实测，
 *    只有列表完整时才计算运行/待机；缺失或截断不按待机补齐。
 * 2. 设备运行统计取全量 pageAir 实时快照；楼层空间监测只接入南都 10F 极派＆云脉区 4 台真实内机，其余区域仍为演示。
 * 3. 室温舒适占比 = 在线内机室温在 22–26℃ 的占比，属本屏自定义口径。
 */

import { REAL_AC_ZONE_ID } from './air-model'

export const REAL_AC_PROJECT_ID = '1000107'
export { REAL_AC_ZONE_ID }
export const REAL_AC_UNIT_NAMES = ['10F-03-04', '10F-03-05', '10F-03-06', '10F-03-07']
export const COMFORT_MIN = 22
export const COMFORT_MAX = 26

const MODE_BY_VALUE = { 0: 'auto', 1: 'heat', 2: 'cool', 4: 'fan', 8: 'dry' }
const SPEED_BY_VALUE = { 0: 'auto', 1: 'low', 2: 'medium', 3: 'high' }
export const MODE_LABELS = { auto: '自动', heat: '制热', cool: '制冷', fan: '送风', dry: '除湿' }
export const SPEED_LABELS = { auto: '自动', low: '低风', medium: '中风', high: '高风' }

function num (value) {
  if (value == null || value === '' || typeof value === 'boolean') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function round1 (value) {
  return Number(Number(value).toFixed(1))
}

export function isAcOffline (row) {
  if (!row) return true
  return Number(row.state) === 1 || row.faultCode === 'LOST'
}

export function acStateOf (device) {
  if (!device || !device.online) return 'offline'
  return device.power ? 'run' : 'standby'
}

export function acModeId (value) {
  return MODE_BY_VALUE[Number(value)] || 'auto'
}

export function acSpeedId (value) {
  return SPEED_BY_VALUE[Number(value)] || 'auto'
}

/** 单项目设备列表 → 运行/待机/离线与室温分布（仅用于列表能覆盖到的设备）。 */
export function summarizeAcRows (rows = []) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : []
  const offlineList = list.filter(isAcOffline)
  const onlineList = list.filter(row => !isAcOffline(row))
  const running = onlineList.filter(row => Number(row.powerStatus) === 1).length
  const temps = onlineList
    .map(row => num(row.roomTemperature))
    .filter(value => value != null && value > 0)
  return {
    measuredTotal: list.length,
    measuredOffline: offlineList.length,
    measuredOnline: onlineList.length,
    running,
    standby: Math.max(0, onlineList.length - running),
    temps,
    avgTemp: temps.length ? round1(temps.reduce((total, value) => total + value, 0) / temps.length) : null,
    comfort: comfortRate(temps)
  }
}

export function comfortRate (temps = []) {
  const list = temps.filter(value => Number.isFinite(value))
  if (!list.length) return null
  const inside = list.filter(value => value >= COMFORT_MIN && value <= COMFORT_MAX).length
  return round1(inside / list.length * 100)
}

/** 当前在线内机室温分布；仅用于空调运行卡，不用任何模拟温度。 */
export function temperatureBins (rows = []) {
  const temps = (Array.isArray(rows) ? rows : [])
    .filter(row => row && !isAcOffline(row))
    .map(row => num(row.roomTemperature))
    .filter(value => value != null && value > 0)
  const bins = [
    { label: '<20', test: value => value < 20 },
    { label: '20–22', test: value => value >= 20 && value < 22 },
    { label: '22–24', test: value => value >= 22 && value < 24 },
    { label: '24–26', test: value => value >= 24 && value <= 26 },
    { label: '26–28', test: value => value > 26 && value <= 28 },
    { label: '>28', test: value => value > 28 }
  ]
  const comfort = temps.filter(value => value >= COMFORT_MIN && value <= COMFORT_MAX).length
  return {
    labels: bins.map(bin => bin.label),
    values: bins.map(bin => temps.filter(bin.test).length),
    total: temps.length,
    comfort,
    rate: comfortRate(temps),
    average: temps.length ? round1(temps.reduce((sum, value) => sum + value, 0) / temps.length) : null
  }
}

/**
 * 汇总多个项目。total/offline 优先取设备统计；列表仅在完整时参与状态汇总。
 */
export function combineAcStatus (entries = []) {
  const list = Array.isArray(entries) ? entries : []
  if (!list.length) return null
  const rows = list.flatMap(entry => entry.rows || [])
  const measured = summarizeAcRows(rows)
  const counts = list.map(entry => {
    const entryRows = entry.rows || []
    const complete = entry.ok === true && !entry.truncated &&
      (entry.listTotal == null || entryRows.length === entry.listTotal) &&
      (entry.total == null || entryRows.length === entry.total) &&
      entryRows.every(row => isAcOffline(row) || [0, 1].includes(num(row.powerStatus)))
    return {
      complete,
      total: entry.total != null ? entry.total : (entry.listTotal != null ? entry.listTotal : complete ? entryRows.length : null),
      offline: entry.offline != null ? entry.offline : complete ? entryRows.filter(isAcOffline).length : null
    }
  })
  const sum = key => counts.every(entry => entry[key] != null) ? counts.reduce((value, entry) => value + entry[key], 0) : null
  const total = sum('total')
  const offline = sum('offline')
  const partial = counts.some(entry => !entry.complete)
  const online = total != null && offline != null ? Math.max(0, total - offline) : null
  const running = !partial && online != null ? Math.min(measured.running, online) : null
  const standby = running != null ? Math.max(0, online - running) : null
  return {
    total,
    offline,
    online,
    running,
    standby,
    measuredTotal: measured.measuredTotal,
    measuredRunning: measured.running,
    avgTemp: measured.avgTemp,
    comfort: partial ? null : measured.comfort,
    partial,
    hasTotal: total != null
  }
}

/** 南都 10F 极派＆云脉区的 4 台真实内机（1=04、2=05、3=06、4=07）。 */
export function pickZoneDevices (rows = []) {
  const map = new Map()
  ;(Array.isArray(rows) ? rows : []).forEach(row => {
    const name = String((row && row.name) || '').trim()
    if (name) map.set(name, row)
  })
  return REAL_AC_UNIT_NAMES.map(name => {
    const row = map.get(name)
    if (!row) return null
    const offline = isAcOffline(row)
    const setpoint = num(row.targetTemperature)
    return {
      id: `real-${row.id != null ? row.id : name}`,
      zoneId: REAL_AC_ZONE_ID,
      name,
      serial: String(row.serial || row.sequence || row.serialNumber || ''),
      real: true,
      tenantId: row.tenantId == null ? null : String(row.tenantId),
      tenantName: row.tenantName || '',
      online: !offline,
      power: Number(row.powerStatus) === 1,
      mode: acModeId(row.operationMode),
      speed: acSpeedId(row.fanSpeed),
      setpoint,
      roomTemperature: num(row.roomTemperature)
    }
  }).filter(Boolean)
}

export function normalizeAirDaily (records = [], range = null) {
  const rows = Array.isArray(records) ? records : []
  const totals = new Map()
  const add = (date, value) => {
    const key = String(date || '').slice(0, 10)
    if (!key) return
    if (range && (key < range.startDate || key > range.endDate)) return
    const amount = num(value)
    if (amount == null) return
    totals.set(key, (totals.get(key) || 0) + amount)
  }
  rows.forEach(row => {
    if (row && row.dateDataMap && typeof row.dateDataMap === 'object') {
      Object.keys(row.dateDataMap).forEach(date => add(date, row.dateDataMap[date] && row.dateDataMap[date].useElec))
      return
    }
    if (row && Array.isArray(row.settleAirDateDtoList)) {
      row.settleAirDateDtoList.forEach(item => add(item && item.date, item && item.useElec))
    }
  })
  const dates = Array.from(totals.keys()).sort()
  return {
    labels: dates.map(date => `${date.slice(5, 7)}/${date.slice(8, 10)}`),
    values: dates.map(date => Number(totals.get(date).toFixed(2))),
    total: dates.length ? Number(dates.reduce((sum, date) => sum + totals.get(date), 0).toFixed(2)) : null
  }
}
